import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react'

import ReactDatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

import { isAfter, isBefore } from 'date-fns'
import { enGB, lt, lv, nl, pl, ru } from 'date-fns/locale'

import CalendarIcon from '@/atoms/icons/calendar.svg'
import { Box, Flex } from '@/atoms/layout'
import { ErrorMessage } from '@/components/form-elements'
import { FieldLabel } from '@/components/form-elements/field-label'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_FORMAT,
  DATE_INPUT_MASK,
  DATE_PLACEHOLDER,
  getMaxDate,
  getMinDate,
  getValidDate,
} from '@/helpers/date'
import { AdornmentPosition } from '@/models/common'
import { Space } from '@/styles'
import i18n from '@/translations'

import { DatepickerHeader, DatepickerHeaderProps, StyledDatePicker } from './atoms'
import PatternInput from './pattern-input'

const locales = {
  lt,
  lv,
  nl,
  en: enGB,
  ru,
  pl,
}

interface Props {
  maxDate?: Date | string
  minDate?: Date | string
  startDate?: Date | string
  startLabel?: string
  startError?: string
  endDate?: Date | string
  endLabel?: string
  endError?: string
  onChange?: (date: { startDate: Date; endDate: Date }) => void
}

const DateRangePickerComponent: FunctionComponent<Props> = ({
  maxDate: initialMaxDate,
  minDate: initialMinDate,
  startDate,
  endDate,
  onChange,
  startError,
  startLabel,
  endLabel,
  endError,
}) => {
  const [start, setStartDate] = useState(getValidDate(startDate))
  const [end, setEndDate] = useState(getValidDate(endDate))
  const minDate = getMinDate(initialMinDate)
  const maxDate = getMaxDate(initialMaxDate)

  useEffect(() => {
    setStartDate(getValidDate(startDate))
    setEndDate(getValidDate(endDate))
  }, [startDate, endDate])

  const getCustomHeader = (props: DatepickerHeaderProps): ReactElement => (
    <DatepickerHeader params={props} locale={locales[i18n.language]} minDate={minDate} maxDate={maxDate} />
  )

  const handleChangeStartDate = (newStartDate: Date): void => {
    const validStartDate = end ? (isBefore(newStartDate, end) ? newStartDate : start) : newStartDate
    setStartDate(validStartDate)
    onChange({ startDate: validStartDate, endDate: end })
  }
  const handleChangeEndDate = (newEndDate: Date): void => {
    const validEndDate = start ? (isAfter(newEndDate, start) ? newEndDate : end) : newEndDate
    setEndDate(validEndDate)
    onChange({ startDate: start, endDate: validEndDate })
  }

  return (
    <Flex>
      <Box width={1 / 2} pr={Space.S12}>
        {startLabel && <FieldLabel id="startDate" label={startLabel} mb={Space.S4} />}
        <StyledDatePicker>
          <ReactDatePicker
            placeholderText={DATE_PLACEHOLDER}
            dateFormat={DATE_DISPLAY_FORMAT}
            showPopperArrow={false}
            popperPlacement="bottom"
            selectsStart
            startDate={start}
            endDate={end}
            onChange={handleChangeStartDate}
            customInput={
              <PatternInput
                id="startDate"
                error={!!startError}
                adornment={<CalendarIcon />}
                adornmentPosition={AdornmentPosition.START}
                format={DATE_INPUT_FORMAT}
                mask={DATE_INPUT_MASK}
              />
            }
            locale={locales[i18n.language]}
            maxDate={maxDate}
            minDate={minDate}
            renderCustomHeader={getCustomHeader}
            selected={start}
          />
        </StyledDatePicker>
        {!!startError && <ErrorMessage error={startError} />}
      </Box>
      <Box width={1 / 2} pl={Space.S12}>
        {endLabel && <FieldLabel id="endDate" label={endLabel} mb={Space.S4} />}
        <StyledDatePicker>
          <ReactDatePicker
            placeholderText={DATE_PLACEHOLDER}
            dateFormat={DATE_DISPLAY_FORMAT}
            showPopperArrow={false}
            popperPlacement="bottom"
            selectsEnd
            startDate={start}
            endDate={end}
            onChange={handleChangeEndDate}
            customInput={
              <PatternInput
                id="endDate"
                error={!!endError}
                adornment={<CalendarIcon />}
                adornmentPosition={AdornmentPosition.START}
                format={DATE_INPUT_FORMAT}
                mask={DATE_INPUT_MASK}
              />
            }
            locale={locales[i18n.language]}
            maxDate={maxDate}
            minDate={start}
            renderCustomHeader={getCustomHeader}
            selected={end}
          />
        </StyledDatePicker>
        {!!endError && <ErrorMessage error={endError} />}
      </Box>
    </Flex>
  )
}

export default DateRangePickerComponent
